import { useContext, useEffect, useRef, useState } from 'react'

import { ActionIcon, Menu, Textarea, Tooltip } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import EmojiPicker from 'src/components/Inputs/EmojiPicker/EmojiPicker'
import MentionHighlightBackdrop from 'src/components/Inputs/MentionHighlightBackdrop/MentionHighlightBackdrop'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import { ActivityLogType } from 'src/graphql/types/activityLogs'
import IconCheckmark from 'src/icons/IconCheckmark'
import { buildMentionTags } from 'src/lib/mentions.utils'
import { NOTE_MAX_LENGTH } from 'src/lib/notes'
import {
  MENTION_DELIMITER,
  replaceNamesWithRecruitersId,
} from 'src/lib/recruiters.utils'
import { focusCaretAfterInsert, insertAtCaret } from 'src/lib/textInput.utils'

import FollowUpMenu from './components/FollowUpMenu'
import NoteTemplateMenu from './components/NoteTemplateMenu'

interface NoteInputProps {
  onCreate: (values: Record<string, unknown>) => unknown
  applicantId?: string
}

const NoteInput = ({ onCreate, applicantId }: NoteInputProps) => {
  const inputRef = useRef(null)
  // Set by programmatic inserts (emoji, template) so the @mention effect
  // below does not open on whatever word happens to end the note.
  const skipMentionCheckRef = useRef(false)
  const [opened, setOpened] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedWord, setSelectedWord] = useState('')
  const recruiters = useContext(RecruitersContext)
  const noteInputSchema = z.object({
    content: z
      .string()
      .min(1, { message: 'Enter at least 1 character.' })
      .max(NOTE_MAX_LENGTH, `Character limit is ${NOTE_MAX_LENGTH}.`),
  })

  const form = useForm({
    initialValues: {
      content: '',
    },
    validateInputOnChange: true,
    validate: zodResolver(noteInputSchema),
  })

  const handleEmojiSelect = (emoji: string) => {
    const { content, caret } = insertAtCaret(
      inputRef.current,
      // useForm infers this field as unknown here, matching how the rest of
      // this file guards it.
      String(form.values.content ?? ''),
      emoji
    )
    skipMentionCheckRef.current = true
    form.setFieldValue('content', content)
    focusCaretAfterInsert(inputRef.current, caret)
  }

  // A picked template fills an empty note outright; when the recruiter has
  // already typed something it is spliced in at the caret so nothing is lost.
  const handleTemplateSelect = (templateContent: string) => {
    // A template made only of assignee variables can resolve to nothing.
    if (!templateContent) {
      toast('This template has nothing to add for this applicant.', 'warning')
      return
    }
    const current = String(form.values.content ?? '')
    skipMentionCheckRef.current = true
    if (!current.trim()) {
      form.setFieldValue('content', templateContent)
      focusCaretAfterInsert(inputRef.current, templateContent.length)
      return
    }

    const { content, caret } = insertAtCaret(
      inputRef.current,
      current,
      templateContent
    )
    // A template ending in a mention glued to the next word ("@Alice
    // Johnsonnow") is not recognised on save, so keep a delimiter between
    // the insert and what follows it.
    const nextChar = content.charAt(caret)
    const needsSpace =
      !MENTION_DELIMITER.test(templateContent.slice(-1)) &&
      nextChar !== '' &&
      !MENTION_DELIMITER.test(nextChar)
    const adjusted = needsSpace
      ? `${content.slice(0, caret)} ${content.slice(caret)}`
      : content
    form.setFieldValue('content', adjusted)
    focusCaretAfterInsert(inputRef.current, caret + (needsSpace ? 1 : 0))
  }

  const onFormSubmit = (values, followUpDueDate?: Date) => {
    if (saving) return
    // Converted into a copy: mutating the form's values would flash the raw
    // `@{{employee:id}}` tokens in the textarea until the save completes.
    const content = replaceNamesWithRecruitersId(values.content, recruiters)
    setSaving(true)
    const result = onCreate({
      type: ActivityLogType.NOTE,
      tags: buildMentionTags(content),
      ...values,
      content,
      ...(followUpDueDate && { followUp: { dueDate: followUpDueDate } }),
    })
    // Clear only once the save went through: with onError set, Apollo
    // resolves a failed mutation with no data, and the note must survive.
    Promise.resolve(result)
      .then((outcome) => {
        if (outcome === undefined || outcome?.data) form.reset()
      })
      .finally(() => setSaving(false))
  }

  const saveWithFollowUp = (dueDate: Date) => {
    if (saving || form.validate().hasErrors) return
    onFormSubmit({ ...form.values }, dueDate)
  }

  const canSave = form.isValid() && !saving

  useEffect(() => {
    if (skipMentionCheckRef.current) {
      skipMentionCheckRef.current = false
      setOpened(false)
      return
    }
    const cursorPosition = inputRef.current.selectionStart
    const textBeforeCursor = form.values.content.slice(0, cursorPosition)
    const temp = textBeforeCursor.split(/\s+/)
    const currentWord = temp[temp.length - 1]
    if (currentWord?.startsWith('@')) {
      setSelectedWord(currentWord.slice(1))
      setOpened(true)
    } else {
      setOpened(false)
    }
  }, [form.values.content])

  const actions = (
    <div className="flex flex-row items-center gap-1">
      {applicantId && (
        <NoteTemplateMenu
          applicantId={applicantId}
          onTemplateSelect={handleTemplateSelect}
        />
      )}
      <EmojiPicker onSelect={handleEmojiSelect} />
      <div className="flex flex-row items-stretch">
        <Tooltip label="Save note" position="top" className="text-xs">
          <ActionIcon
            type="submit"
            disabled={!canSave}
            variant="transparent"
            size={28}
            radius={0}
            aria-label="Save note"
            className="rounded-l-md bg-doubleNickel-brand-500 transition-colors hover:bg-doubleNickel-brand-700 focus-visible:-outline-offset-2 disabled:bg-doubleNickel-gray-100"
          >
            <IconCheckmark
              className={`h-4 w-4 fill-none ${
                canSave
                  ? 'stroke-doubleNickel-white'
                  : 'stroke-doubleNickel-gray-400'
              }`}
            />
          </ActionIcon>
        </Tooltip>
        <FollowUpMenu disabled={!canSave} onSelect={saveWithFollowUp} />
      </div>
    </div>
  )

  return (
    <form onSubmit={form.onSubmit((values) => onFormSubmit(values))}>
      <div className="flex flex-col gap-1 rounded-lg border px-2">
        <StyledMenu opened={opened}>
          <Menu.Target>
            <div className="relative flex-1">
              <MentionHighlightBackdrop
                content={String(form.values.content ?? '')}
                recruiters={recruiters}
                textareaRef={inputRef}
              />
              <Textarea
                ref={inputRef}
                variant="unstyled"
                placeholder="Write a note here..."
                autosize={true}
                maxRows={4}
                rightSectionWidth={applicantId ? 116 : 84}
                rightSectionPointerEvents="all"
                rightSection={actions}
                className="relative z-10"
                withAsterisk
                value={form.values.content}
                error={form.errors.content}
                onChange={(e) => {
                  form.setFieldValue('content', e.currentTarget.value)
                }}
              />
            </div>
          </Menu.Target>
          <Menu.Dropdown
            className="max-h-[300px] overflow-y-auto"
            onFocus={() => {
              inputRef.current.focus()
            }}
          >
            {recruiters
              .filter((recruiter) =>
                recruiter.label
                  .toLowerCase()
                  .includes(selectedWord.toLowerCase())
              )
              .map((recruiter) => (
                <Menu.Item
                  key={recruiter.value}
                  value={recruiter.value}
                  onClick={() => {
                    const content = form.values.content
                    const cursorPosition = inputRef.current.selectionStart
                    const textBeforeCursor = content.slice(0, cursorPosition)
                    const temp = textBeforeCursor.split(/\s+/)
                    const currentWord = temp[temp.length - 1]
                    const newContent =
                      content.slice(0, cursorPosition - currentWord.length) +
                      `@${recruiter.label}` +
                      content.slice(cursorPosition)
                    form.setFieldValue('content', newContent)
                    setOpened(false)
                  }}
                >
                  {recruiter.label}
                </Menu.Item>
              ))}
          </Menu.Dropdown>
        </StyledMenu>
      </div>
    </form>
  )
}

export default NoteInput
